/**
 * Formatters Module
 *
 * Provides formatting utilities for text, dates, times, and display values.
 */

import { Dimensions } from 'react-native';

import { t as t18n } from '../../i18n';

import { formatDate, formatTime } from './localeFormat';

const screenWidth = Dimensions.get('window').width;

/**
 * Calculate dynamic font size based on text length
 * @param {string} text - The text to measure
 * @param {number} baseValue - Base value for calculation
 * @param {number} userFontSize - User's font size preference
 * @returns {number} The calculated font size
 */
export function calculateFontSize(text, baseValue, userFontSize) {
  const minFontSize = 10;
  const maxFontSize = 24;
  const emailLength = text.length;
  const base = screenWidth >= 1000 ? baseValue : baseValue * (screenWidth / 1000);

  if (emailLength < base) {
    return maxFontSize;
  }

  const excessLength = emailLength - base;
  let decayFactor =
    userFontSize < 14 ? 0.032 : userFontSize < 15 ? 0.035 : userFontSize < 16 ? 0.038 : 0.04;

  decayFactor *= screenWidth >= 1000 ? 1 : 500 / screenWidth;

  const newSize = maxFontSize * Math.exp(-decayFactor * excessLength);

  return Math.min(Math.max(minFontSize, newSize), maxFontSize);
}

/**
 * Format a UUID to show only the first 4 characters
 * @param {string} text - The UUID string
 * @returns {string} The formatted short UUID
 */
export function formatUuid(text) {
  if (!text) {
    return '';
  }
  return text
    .substr(0, 4)
    .replace(/[\r\n]/g, ' ')
    .trim();
}

/**
 * Format description text by removing newlines
 * @param {string} text - The description text
 * @returns {string} The formatted description
 */
export function formatDescription(text) {
  return text?.replace(/[\r\n]/g, ' ').trim();
}

/**
 * Extract just the value property from chip objects
 * @param {Array<Object>} chips - Array of chip objects with 'value' property
 * @returns {Array<string>} Array of chip values
 */
export function textOnly(chips) {
  return chips.map((e) => e.value);
}

/**
 * Convert 24-hour time to 12-hour format with AM/PM
 *
 * @deprecated AM/PM is an English convention — Spanish uses a 24-hour clock.
 * Use `formatTime` from util/format, which follows the active locale. Kept
 * because it is part of the public `Util` surface; it has no callers left.
 *
 * @param {string} time - Time in "HH:MM" format
 * @returns {string} Time in "H:MM AM/PM" format
 */
export function convertTo12HourTime(time) {
  let timeArray = time.split(':');

  let hours = parseInt(timeArray[0]);
  let minutes = parseInt(timeArray[1]);

  let meridiem = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12 || 12;

  if (minutes < 10) {
    minutes = '0' + minutes;
  }

  return hours + ':' + minutes + ' ' + meridiem;
}

/**
 * Convert a timestamp to a human-readable date/time string.
 *
 * Shows "Today", "Yesterday", a day within this year, or a full date — each
 * followed by the time. The time uses the locale's own clock convention, so
 * English readers get "3:05 PM" and Spanish readers get "15:05".
 *
 * @param {number} t - Unix timestamp in milliseconds
 * @param {Object} [options]
 * @param {string} [options.locale] - pin the output language instead of using
 *   the active one. Pass 'en' for text bound for someone else — an email body,
 *   say — where the reader's language is not the sender's.
 * @returns {string} Formatted date/time string
 */
export function timeConverter(t, options = {}) {
  const { locale } = options;
  const value = new Date(t);
  const today = new Date();
  const yesterday = new Date(Date.now() - 86400000);

  const time = formatTime(value, { locale });

  const atMidnight = (d) => new Date(d).setHours(0, 0, 0, 0);
  let day;

  if (atMidnight(value) === atMidnight(today)) {
    day = t18n('common:time.today', { lng: locale });
  } else if (atMidnight(value) === atMidnight(yesterday)) {
    day = t18n('common:time.yesterday', { lng: locale });
  } else if (value.getFullYear() === today.getFullYear()) {
    day = formatDate(value, { month: 'short', day: 'numeric', locale });
  } else {
    day = formatDate(value, { month: 'short', day: 'numeric', year: 'numeric', locale });
  }

  return day + ', ' + time;
}
